import {
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  LOCALE_ID,
  OnInit,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { PharmacyFilters } from '../../../../../services/pharmacy/pharmacy.model';
import { FormControl, FormGroup, FormsModule, NgModel, ReactiveFormsModule } from '@angular/forms';
import { LookupVTO } from '../../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { PharmacyService } from '../../../../../services/pharmacy/pharmacy.service';
import { debounceTime, distinctUntilChanged, filter, finalize, Observable, switchMap, tap } from 'rxjs';
import { Loading } from '../../../../../utils/loading/loading';
import { Error } from '../../../../public-user-component/auth/shared/error/error';
import { AuthService } from '../../../../../services/auth-service';
import { ɵɵDir } from '@angular/cdk/scrolling';
import { dateRangeValidator } from './utils/today';

import { HasScopeDirective } from '../../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../../services/scope/scope-constants';

@Component({
  selector: 'app-filter',
  imports: [FormsModule, ReactiveFormsModule, Loading, Error, HasScopeDirective],
  templateUrl: './filter.html',
  styleUrl: './filter.scss',
  standalone: true,
})
export class Filter implements OnInit {
  private pharmacyService = inject(PharmacyService);
  private userMgt = inject(AuthService);
  private locale = inject(LOCALE_ID);
  private el = inject(ElementRef);

  userId = signal<number>(0);
  showStatusDropdown = signal<boolean>(false);
  selectedStatusIds = signal<number[] | undefined>([]);

  filterForm = new FormGroup(
    {
      searchControl: new FormControl(''),
      searchEmployeeControl: new FormControl(''),
      statusId: new FormControl<number[] | undefined>(undefined),
      createdOnFrom: new FormControl<string>(''),
      createdOnTo: new FormControl<string>(''),
      lastModifiedOnFrom: new FormControl<string>(''),
      lastModifiedOnTo: new FormControl<string>(''),
    },
    {
      validators: [
        dateRangeValidator('createdOnFrom', 'createdOnTo'),
        dateRangeValidator('lastModifiedOnFrom', 'lastModifiedOnTo'),
      ],
    },
  );

  filters = input.required<PharmacyFilters>();
  statuses = input.required<LookupVTO[]>();

  readonly SCOPES = SCOPES;

  // AUTO COMPLETE PHARMACIST

  ownerSearchValue = signal<string>('');
  pharamcyOwnerResults = signal<{ id: number; fullName: string }[]>([]);
  activeDropDown = signal<boolean>(false);
  loadingPharamcyOwners = signal<boolean>(false);
  errorPharamcyOwners = signal<boolean>(false);
  selectedPharmacyOwner = signal<string>('');

  // AUTO COMPLETE EMPLOYEE
  employeeSearchValue = signal<string>('');
  employeeResults = signal<{ id: number; fullName: string }[]>([]);
  activeDropDownEmployees = signal<boolean>(false);
  loadingEmployeess = signal<boolean>(false);
  errorEmployeess = signal<boolean>(false);
  selectedEmployee = signal<string>('');

  toggleSortOrder = output();
  resetFilters = output();
  applyFilters = output<{
    createdOnFrom?: string;
    createdOnTo?: string;
    lastModifiedOnFrom?: string;
    lastModifiedOnTo?: string;
    statusIds?: number[];
  }>();
  // statusChanged = output<number | undefined>();
  selectFullName = output<{ id: number; fullName: string }>();
  selectEmployee = output<{ id: number; fullName: string }>();
  // toggleToMe = output<number>();

  private setupAutoComplete<T>(
    controlName: string,
    serviceCall: (value: string) => Observable<{ list: T[] }>,
    loading: WritableSignal<boolean>,
    error: WritableSignal<boolean>,
    activeDropDown: WritableSignal<boolean>,
    results: WritableSignal<T[]>,
  ): void {
    this.filterForm
      .get(controlName)!
      .valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((value) => {
          if (!value || value.trim() === '') {
            loading.set(false);
            error.set(false);
            activeDropDown.set(false);
            results.set([]);
          }
        }),
        filter((value): value is string => !!value && value.trim() !== ''),
        tap(() => {
          loading.set(true);
          error.set(false);
          activeDropDown.set(true);
        }),
        switchMap((value) => serviceCall(value).pipe(finalize(() => loading.set(false)))),
      )
      .subscribe({
        next: (res: any) => results.set(res.list),
        error: () => error.set(true),
      });
  }

  ngOnInit(): void {
    this.setupAutoComplete(
      'searchControl',
      (value) => this.pharmacyService.pharmacyOwnerAutoComplete(value),
      this.loadingPharamcyOwners,
      this.errorPharamcyOwners,
      this.activeDropDown,
      this.pharamcyOwnerResults,
    );

    this.setupAutoComplete(
      'searchEmployeeControl',
      (value) => this.pharmacyService.pharmacyRequestEmployeeAutoComplete(value),
      this.loadingEmployeess,
      this.errorEmployeess,
      this.activeDropDownEmployees,
      this.employeeResults,
    );

    this.filterForm.get('statusId')!.valueChanges.subscribe((statusId) => {
      // console.log('statusId:', statusId);
      // this.statusChanged.emit(statusId ?? undefined);
    });

    const user_id = this.userMgt.getUserId();
    this.userId.set(user_id!);
  }

  onCancelDropDown() {
    this.activeDropDown.set(false);
  }
  onToggleSortOrder() {
    this.toggleSortOrder.emit();
  }
  onResetFilters() {
    this.resetFilters.emit();
    this.filterForm.reset();
    this.selectedStatusIds.set([]);
  }
  onApplyFilters() {
    if (this.filterForm.invalid) {
      this.filterForm.markAllAsTouched();
      return;
    }

    const { createdOnFrom, createdOnTo, lastModifiedOnFrom, lastModifiedOnTo } = this.filterForm.value;

    this.applyFilters.emit({
      createdOnFrom: createdOnFrom || undefined,
      createdOnTo: createdOnTo || undefined,
      lastModifiedOnFrom: lastModifiedOnFrom || undefined,
      lastModifiedOnTo: lastModifiedOnTo || undefined,
      statusIds: this.selectedStatusIds(),
    });
  }

  onSelectFullName(item: { id: number; fullName: string }) {
    this.selectFullName.emit(item);
    this.activeDropDown.set(false);
    this.selectedPharmacyOwner.set(item.fullName);
    this.filterForm.get('searchControl')!.setValue(item.fullName, { emitEvent: false });
  }

  onSelectEmployee(item: { id: number; fullName: string }) {
    this.selectEmployee.emit(item);
    this.activeDropDownEmployees.set(false);
    this.selectedEmployee.set(item.fullName);
    this.filterForm.get('searchEmployeeControl')!.setValue(item.fullName, { emitEvent: false });
  }
  isArabic() {
    return this.locale.startsWith('ar');
  }
  isEnglish() {
    return this.locale.startsWith('en');
  }

  // STATUS DROP DOWN
  toggleStatusDropdown() {
    this.showStatusDropdown.set(!this.showStatusDropdown());
  }

  toggleStatus(id: number | undefined) {
    if (id === undefined) {
      this.selectedStatusIds.set([]);
      return;
    }
    const current = this.selectedStatusIds() ?? [];
    const index = current.indexOf(id);
    if (index === -1) {
      this.selectedStatusIds.set([...current, id]);
    } else {
      this.selectedStatusIds.set(current.filter((existingId) => existingId !== id));
    }
  }

  isStatusSelected(id: number | undefined): boolean {
    if (id === undefined) return (this.selectedStatusIds() ?? []).length === 0;
    return this.selectedStatusIds()?.includes(id) ?? false;
  }
  closeStatusDropdown() {
    this.showStatusDropdown.set(false);
  }

  // TOGGLE ASSIGNED TO ME
  // assignToMe = signal<boolean>(false);

  // onActivateAssignTomMe() {
  //   this.assignToMe.set(true);
  //   this.toggleToMe.emit(this.userId());
  // }
  // onDeActivateAssignToMe() {
  //   this.assignToMe.set(false);
  //   this.toggleToMe.emit(0);
  // }

  // UTILS

  today(): string {
    return new Date().toISOString().split('T')[0]; // "2025-02-22"
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const dropdown = this.el.nativeElement.querySelector('.status-dropdown-container');
    if (dropdown && !dropdown.contains(event.target)) {
      this.closeStatusDropdown();
    }
  }
}
